<script setup lang="ts">
import { CheckCircleIcon, DotsCircleHorizontalIcon, XCircleIcon } from '@heroicons/vue/solid'
import SignteqLogo from '../../../../asset/SignteqLogo.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import LoadingSpinner from '../../../base/LoadingSpinner.vue'
import {
    ExternalKycProcessResponse,
    getExternalKycProcess,
    postExternalKycProcess,
    setExternalKycProcessStatus,
} from '../../../../utilities/api'
import { VerificationStatus } from '../../../../typings'
import { i18n } from '../../../../localization'
import AccountVerificationOverrideData from './AccountVerificationOverrideData.vue'
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'

const verificationStatus = ref<VerificationStatus>('not_started')
const verificationLink = ref('')
const iFrameHeight = ref('100px')

const loading = ref(false)
const externalKycProcessId = ref<string>('')
const externalKycProcessUpdatedAt = ref<string>('')
const externalKycProcess = ref<ExternalKycProcessResponse | null>(null)
const showOverrideData = computed(() => verificationStatus.value === 'success')

const failedCanceledText = computed(() => {
    return i18n.global.t('verification.status_user_canceled')
})

const failedCanceledDescription = computed(() => {
    if (externalKycProcess.value && externalKycProcess.value.status === 'failed_canceled') {
        switch (externalKycProcess.value.error_message) {
            case 'loading':
                return i18n.global.t('verification.status_user_canceled_loading_description')
            case 'unavailable':
                return i18n.global.t('verification.status_user_canceled_unavailable_description')
            case 'privacy':
                return i18n.global.t('verification.status_user_canceled_privacy_description')
            case 'unclear':
                return i18n.global.t('verification.status_user_canceled_unclear_description')
            case 'technical':
                return i18n.global.t('verification.status_user_canceled_technical_description')
            case 'other':
                // The investor's own free-text reason, when they provided one, is more useful than the generic copy.
                return (
                    externalKycProcess.value.error_custom ||
                    i18n.global.t('verification.status_user_canceled_other_description')
                )
        }
    }
    return i18n.global.t('verification.status_user_canceled_unknown_description')
})

const verificationStatusOptions = computed(() => ({
    not_started: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_not_started'),
        description: i18n.global.t('verification.status_not_started_description'),
        class: 'text-gray-400',
    },
    created: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_created'),
        description: i18n.global.t('verification.status_created_description'),
        class: 'text-blue-400',
    },
    pending: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_created'),
        description: i18n.global.t('verification.status_created_description'),
        class: 'text-blue-400',
    },
    in_review: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_in_review'),
        description: i18n.global.t('verification.status_in_review_description'),
        class: 'text-orange-400',
    },
    success: {
        text: i18n.global.t('verification.status_success'),
        description: i18n.global.t('verification.status_success_description'),
        icon: CheckCircleIcon,
        class: 'text-green-400',
    },
    failed_canceled: {
        icon: XCircleIcon,
        text: failedCanceledText.value,
        description: failedCanceledDescription.value,
        class: 'text-red-400',
    },
    failed_fraudulent: {
        icon: XCircleIcon,
        text: i18n.global.t('verification.status_failure'),
        description: i18n.global.t('verification.status_failure_description'),
        class: 'text-red-400',
    },
}))

const currentVerificationStatus = computed(() => {
    return verificationStatusOptions.value[verificationStatus.value]
})

const buttonText = computed(() => {
    if (verificationStatus.value === 'created' || verificationStatus.value === 'pending') {
        return i18n.global.t('verification.signteq_kyc_restart_button')
    } else if (verificationStatus.value === 'in_review') {
        return i18n.global.t('verification.signteq_kyc_waiting_for_id_now_button')
    } else if (verificationStatus.value === 'failed_canceled' || verificationStatus.value === 'failed_fraudulent') {
        return i18n.global.t('verification.signteq_kyc_restart_button')
    } else {
        return i18n.global.t('verification.signteq_kyc_start_button')
    }
})

function getExternalKycProcessStatus(restart = false) {
    if (restart) {
        postExternalKycProcess(restart).then((response) => {
            verificationStatus.value = response.data.status
            verificationLink.value = response.data.redirect_url

            if (response.data.id) {
                externalKycProcessId.value = response.data.id
            }
        })
    }
}

async function fetchExternalKycProcess() {
    const response = await getExternalKycProcess()
    if (response.status === 200) {
        if (response.data.id) {
            externalKycProcessId.value = response.data.id
            externalKycProcess.value = response.data
        }
        externalKycProcessUpdatedAt.value = response.data.updated_at
        verificationStatus.value = response.data.status
        verificationLink.value = response.data.redirect_url
    }
}

async function updateStatus(status: string) {
    if (externalKycProcessId.value !== '') {
        const res = await setExternalKycProcessStatus(externalKycProcessId.value, status)
        verificationStatus.value = res.data.status
    }
}

async function checkMessage(message) {
    // updating iframe height
    if (message.data.type == 'DOCUMENT_HEIGHT') {
        iFrameHeight.value = `${message.data.data.height}px`
    }

    // specific events for the identification process
    if (message.data.type == 'SUCCESS') {
        // Handle success
        // Remove Iframe from DOM
        // Wait for Webhook or fetch result from API
        updateStatus('success')
    }

    if (message.data.type == 'ERROR') {
        //updateStatus('failed_fraudulent')
        //fetchExternalKycProcess()
        verificationLink.value = ''
        console.log('Error message', message.data)
    }

    if (message.data.type == 'USER_CANCELED') {
        // Await in order: the PUT must commit before we GET, otherwise the fetch can win the race and
        // read the pre-cancel state back. The cancellation reason itself arrives separately and
        // asynchronously via Signteq's server-to-server webhook, which may not have landed yet even
        // once this GET resolves - fetchExternalKycProcessWithReasonRetry gives it a few seconds.
        await updateStatus('failed_canceled')
        verificationLink.value = ''
        await fetchExternalKycProcessWithReasonRetry()
    }
}

async function fetchExternalKycProcessWithReasonRetry(attempts = 3, delayMs = 2000) {
    for (let attempt = 0; attempt < attempts; attempt++) {
        await fetchExternalKycProcess()
        if (externalKycProcess.value?.error_message || externalKycProcess.value?.error_custom) {
            return
        }
        if (attempt < attempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, delayMs))
        }
    }
}

let pollExternalKycStatus = null

function startKycProcessPolling() {
    if (
        verificationStatus.value === 'pending' ||
        verificationStatus.value === 'created' ||
        verificationStatus.value === 'in_review'
    ) {
        pollExternalKycStatus = setInterval(() => {
            fetchExternalKycProcess()
            if (
                verificationStatus.value !== 'pending' &&
                verificationStatus.value !== 'created' &&
                verificationStatus.value !== 'in_review'
            ) {
                usePersonalAccountDataState().fetchAccountData()
                clearInterval(pollExternalKycStatus)
            }
        }, 5000)
    }
}

const kycResetThresholdExceeded = computed(() => {
    const hours = Number(window.platformSettings?.kyc_reset_threshold)
    const raw = externalKycProcessUpdatedAt.value
    if (!raw || !Number.isFinite(hours) || hours <= 0) {
        return false
    }
    const updatedAtMs = new Date(raw).getTime()
    if (Number.isNaN(updatedAtMs)) {
        return false
    }
    const thresholdEndMs = updatedAtMs + hours * 60 * 60 * 1000
    return Date.now() > thresholdEndMs
})

const showRestartButton = computed(() => {
    return kycResetThresholdExceeded.value && verificationStatus.value !== 'done' && externalKycProcessId.value !== ''
})

onMounted(async () => {
    await fetchExternalKycProcess()
    startKycProcessPolling()
    window.addEventListener('message', checkMessage)
})

onUnmounted(() => {
    window.removeEventListener('message', checkMessage)
})
</script>

<template>
    <div>
        <div
            class="flex flex-wrap items-center space-y-3 md:space-y-0"
            :class="{ 'pt-4': verificationStatus !== 'success' }">
            <div class="w-full items-center text-center md:w-1/2">
                <button
                    v-if="showRestartButton"
                    type="button"
                    :disabled="loading"
                    class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-2 text-center text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:bg-slate-100"
                    @click="getExternalKycProcessStatus(true)">
                    <LoadingSpinner
                        v-if="verificationStatus === 'in_review' || loading"
                        class="inline-block text-gray-500"></LoadingSpinner>
                    <span class="w-full font-bold">{{ buttonText }}</span>
                </button>
                <button
                    v-if="!externalKycProcessId"
                    type="button"
                    :disabled="loading"
                    class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-2 text-center text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:bg-slate-100"
                    @click="getExternalKycProcessStatus(true)">
                    <LoadingSpinner
                        v-if="verificationStatus === 'in_review' || loading"
                        class="inline-block text-gray-500"></LoadingSpinner>
                    <span class="w-full font-bold">{{ $t('verification.signteq_kyc_start_button') }}</span>
                </button>
                <div class="flex items-center justify-center">
                    via <SignteqLogo class="w-30 my-4 ml-2"></SignteqLogo>
                </div>
            </div>
            <div class="w-full pb-4 md:w-1/2">
                <div class="items-center md:flex">
                    <span class="pr-2 text-sm font-bold text-gray-600"
                        >{{ $t('account_settings.current_status') }}:
                    </span>
                    <div class="inline-flex items-center">
                        <Component
                            :is="currentVerificationStatus.icon"
                            :class="currentVerificationStatus.class"
                            class="inline-block w-5"></Component>
                        <div class="pl-2 text-sm text-gray-600">
                            <p>{{ currentVerificationStatus.text }}</p>
                        </div>
                    </div>
                </div>
                <p class="max-w-sm pt-1 text-xs text-gray-500">{{ currentVerificationStatus.description }}</p>
            </div>
            <iframe
                v-if="
                    verificationLink &&
                    verificationStatus != 'success' &&
                    verificationStatus != 'failed_canceled' &&
                    verificationStatus != 'failed_fraudulent'
                "
                class="w-full pt-8"
                :style="`height: ${iFrameHeight}`"
                :src="verificationLink"
                title="Signteq verification" />
            <div v-if="showOverrideData" class="w-full items-center text-center">
                <AccountVerificationOverrideData></AccountVerificationOverrideData>
            </div>
        </div>
    </div>
</template>
